import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Contact, UserPlus, Search, X, Tag as TagIcon, Trash2, Plus, Mail, Phone, MapPin } from "lucide-react";
import {
  Button, Card, CardBody, Badge, PageHeader, EmptyState, Loading, AccessDenied,
  Field, TextInput, Select, Textarea, Modal,
} from "@/components/redvolt/kit";
import ImportsPage from "@/app/ImportsPage";

const CLASSIFICATION_COLORS = {
  voter: "info", supporter: "implemented", volunteer: "implemented", donor: "warning",
  committee_member: "neutral", elected_official: "neutral", candidate: "neutral",
  prospect: "neutral", event_attendee: "neutral", contact: "neutral",
};

/* ------------------------------- Add Person ------------------------------ */
const AddPersonModal = ({ api, tenantId, open, onClose, onCreated, classTypes }) => {
  const [form, setForm] = useState({ first_name: "", last_name: "", preferred_name: "" });
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [classifications, setClassifications] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleClass = (k) => setClassifications((c) => c.includes(k) ? c.filter((x) => x !== k) : [...c, k]);

  const submit = async () => {
    if (!form.first_name.trim() && !form.last_name.trim() && !form.preferred_name.trim()) {
      toast.error("Enter at least a first or last name"); return;
    }
    setBusy(true);
    try {
      const body = { ...form, classifications };
      if (email.trim()) body.contact = { channel: "email", subtype: "home", value: email.trim() };
      const r = await api.post(`/tenants/${tenantId}/people`, body);
      if (phone.trim()) {
        await api.post(`/tenants/${tenantId}/people/${r.data.id}/contacts`, { channel: "phone", subtype: "mobile", value: phone.trim() });
      }
      toast.success(`Added ${r.data.display_name}`);
      setForm({ first_name: "", last_name: "", preferred_name: "" }); setEmail(""); setPhone(""); setClassifications([]);
      onCreated(r.data.id);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "name_required" ? "A name is required" : "Could not add person");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add a person" description="Add anyone your organization works with — no voter record required." testid="add-person-modal"
      footer={<><Button variant="ghost" onClick={onClose} data-testid="add-person-cancel">Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="add-person-submit"><UserPlus className="mr-1.5 h-4 w-4" />{busy ? "Adding…" : "Add person"}</Button></>}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="First name"><TextInput value={form.first_name} onChange={set("first_name")} data-testid="person-first-name" placeholder="Jane" /></Field>
        <Field label="Last name"><TextInput value={form.last_name} onChange={set("last_name")} data-testid="person-last-name" placeholder="Smith" /></Field>
      </div>
      <Field label="Preferred name" hint="Optional — what they like to be called"><TextInput value={form.preferred_name} onChange={set("preferred_name")} data-testid="person-preferred-name" placeholder="Janie" /></Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Email"><TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="person-email" placeholder="jane@example.com" /></Field>
        <Field label="Phone"><TextInput value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="person-phone" placeholder="(555) 010-0000" /></Field>
      </div>
      <Field label="Relationship to your organization" hint="Choose any that apply">
        <div className="flex flex-wrap gap-1.5">
          {classTypes.map((c) => (
            <button key={c.key} type="button" onClick={() => toggleClass(c.key)} data-testid={`class-toggle-${c.key}`}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${classifications.includes(c.key) ? "border-primary bg-accent text-primary" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </Field>
    </Modal>
  );
};

/* ------------------------------ Person detail ---------------------------- */
const PersonDetail = ({ api, tenantId, personId, caps, tags, onClose, onChanged, classTypes }) => {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [orgRoleDefs, setOrgRoleDefs] = useState([]);
  const canEdit = caps.includes("people:edit");
  const canTags = caps.includes("people:manage_tags");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/tenants/${tenantId}/people/${personId}`); setP(r.data); }
    catch { toast.error("Could not load person"); onClose(); }
    finally { setLoading(false); }
  }, [api, tenantId, personId, onClose]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/tenants/${tenantId}/org-roles`).then((r) => setOrgRoleDefs(r.data)).catch(() => {}); }, [api, tenantId]);

  const addOrgRole = async (roleId) => { try { await api.post(`/tenants/${tenantId}/people/${personId}/org-roles`, { organization_role_id: roleId }); load(); onChanged(); } catch { toast.error("Could not add position"); } };
  const rmOrgRole = async (id) => { try { await api.delete(`/tenants/${tenantId}/people/${personId}/org-roles/${id}`); load(); onChanged(); } catch { toast.error("Could not remove"); } };

  const addClass = async (key) => { try { await api.post(`/tenants/${tenantId}/people/${personId}/classifications`, { classification_key: key }); load(); onChanged(); } catch { toast.error("Could not add"); } };
  const rmClass = async (id) => { try { await api.delete(`/tenants/${tenantId}/people/${personId}/classifications/${id}`); load(); } catch { toast.error("Could not remove"); } };
  const assignTag = async (tagId) => { try { await api.post(`/tenants/${tenantId}/people/${personId}/tags`, { tag_id: tagId }); load(); } catch { toast.error("Could not tag"); } };
  const rmTag = async (ptId) => { try { await api.delete(`/tenants/${tenantId}/people/${personId}/tags/${ptId}`); load(); } catch { toast.error("Could not untag"); } };
  const addNote = async () => { if (!note.trim()) return; try { await api.post(`/tenants/${tenantId}/people/${personId}/notes`, { body: note.trim() }); setNote(""); load(); } catch { toast.error("Could not save note"); } };
  const rmNote = async (id) => { try { await api.delete(`/tenants/${tenantId}/people/${personId}/notes/${id}`); load(); } catch { toast.error("Could not delete note"); } };
  const rmContact = async (id) => { try { await api.delete(`/tenants/${tenantId}/people/${personId}/contacts/${id}`); load(); } catch { toast.error("Could not remove"); } };
  const rmAddress = async (id) => { try { await api.delete(`/tenants/${tenantId}/people/${personId}/addresses/${id}`); load(); } catch { toast.error("Could not remove"); } };
  const deactivate = async () => { try { await api.post(`/tenants/${tenantId}/people/${personId}/deactivate`); toast.success("Person archived"); onChanged(); onClose(); } catch { toast.error("Could not archive"); } };
  const reactivate = async () => { try { await api.post(`/tenants/${tenantId}/people/${personId}/reactivate`); toast.success("Person restored to active"); onChanged(); load(); } catch { toast.error("Could not restore"); } };

  const availClass = (classTypes || []).filter((c) => !(p?.classifications || []).some((x) => x.key === c.key));
  const availTags = (tags || []).filter((t) => !(p?.tags || []).some((x) => x.tag_id === t.id));
  const availOrgRoles = (orgRoleDefs || []).filter((r) => !(p?.organizational_roles || []).some((x) => x.role_id === r.id));

  return (
    <div className="fixed inset-0 z-40" data-testid="person-detail">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-lg flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-bold text-slate-900" data-testid="person-detail-name">{loading ? "Loading…" : p?.display_name}</h2>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-md hover:bg-slate-100" aria-label="Close"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        {loading || !p ? <div className="p-6"><Loading /></div> : (
          <div className="flex-1 space-y-6 overflow-y-auto p-5">
            {p.status !== "active" && <Badge variant="warning">Archived</Badge>}

            <section>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Relationships</p>
              <div className="flex flex-wrap gap-1.5" data-testid="person-classifications">
                {p.classifications.length === 0 && <span className="text-sm text-slate-400">None yet</span>}
                {p.classifications.map((c) => (
                  <span key={c.id} className="inline-flex items-center gap-1">
                    <Badge variant={CLASSIFICATION_COLORS[c.key] || "neutral"}>{c.label}</Badge>
                    {canEdit && <button onClick={() => rmClass(c.id)} className="text-slate-300 hover:text-red-500" aria-label="Remove"><X className="h-3 w-3" /></button>}
                  </span>
                ))}
              </div>
              {canEdit && availClass.length > 0 && (
                <div className="mt-2">
                  <Select onChange={(e) => { if (e.target.value) { addClass(e.target.value); e.target.value = ""; } }} data-testid="add-classification" className="text-sm">
                    <option value="">+ Add relationship…</option>
                    {availClass.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </Select>
                </div>
              )}
            </section>

            <section>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Organizational roles <span className="font-normal normal-case text-slate-400">· positions held (not RedVolt access)</span></p>
              <div className="flex flex-wrap gap-1.5" data-testid="person-org-roles">
                {(p.organizational_roles || []).length === 0 && <span className="text-sm text-slate-400">No positions</span>}
                {(p.organizational_roles || []).map((o) => (
                  <span key={o.id} className="inline-flex items-center gap-1">
                    <Badge variant="neutral">{o.label}</Badge>
                    {canEdit && <button onClick={() => rmOrgRole(o.id)} className="text-slate-300 hover:text-red-500" aria-label="Remove"><X className="h-3 w-3" /></button>}
                  </span>
                ))}
              </div>
              {canEdit && availOrgRoles.length > 0 && (
                <div className="mt-2">
                  <Select onChange={(e) => { if (e.target.value) { addOrgRole(e.target.value); e.target.value = ""; } }} data-testid="add-org-role" className="text-sm">
                    <option value="">+ Add position…</option>
                    {availOrgRoles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </Select>
                </div>
              )}
            </section>

            <section>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Tags</p>
              <div className="flex flex-wrap gap-1.5" data-testid="person-tags">
                {p.tags.length === 0 && <span className="text-sm text-slate-400">No tags</span>}
                {p.tags.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                    <TagIcon className="h-3 w-3" />{t.label}
                    {canTags && <button onClick={() => rmTag(t.id)} className="text-slate-400 hover:text-red-500" aria-label="Remove tag"><X className="h-3 w-3" /></button>}
                  </span>
                ))}
              </div>
              {canTags && availTags.length > 0 && (
                <div className="mt-2">
                  <Select onChange={(e) => { if (e.target.value) { assignTag(e.target.value); e.target.value = ""; } }} data-testid="assign-tag" className="text-sm">
                    <option value="">+ Add tag…</option>
                    {availTags.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </Select>
                </div>
              )}
            </section>

            {p.can_view_contact ? (
              <section>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Contact</p>
                <div className="space-y-1.5" data-testid="person-contacts">
                  {p.contacts.length === 0 && <span className="text-sm text-slate-400">No contact methods</span>}
                  {p.contacts.map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-sm">
                      <span className="flex items-center gap-2 text-slate-700">
                        {c.channel === "email" ? <Mail className="h-4 w-4 text-slate-400" /> : <Phone className="h-4 w-4 text-slate-400" />}
                        {c.value} <span className="text-xs text-slate-400">{c.subtype}</span>
                      </span>
                      {canEdit && <button onClick={() => rmContact(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  ))}
                </div>
                <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Addresses</p>
                <div className="space-y-1.5" data-testid="person-addresses">
                  {p.addresses.length === 0 && <span className="text-sm text-slate-400">No addresses</span>}
                  {p.addresses.map((a) => (
                    <div key={a.id} className="flex items-start justify-between rounded-md border border-slate-100 px-3 py-2 text-sm">
                      <span className="flex items-start gap-2 text-slate-700">
                        <MapPin className="mt-0.5 h-4 w-4 text-slate-400" />
                        <span>{[a.line1, a.line2].filter(Boolean).join(", ")}{(a.line1 || a.city) && <br />}{[a.city, a.state, a.postal_code].filter(Boolean).join(" ")} <span className="text-xs text-slate-400">· {a.address_type.replace("_", " ")}</span></span>
                      </span>
                      {canEdit && <button onClick={() => rmAddress(a.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  ))}
                </div>
              </section>
            ) : (
              <section><p className="text-sm text-slate-400">You don't have permission to view contact details.</p></section>
            )}

            <section>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Notes <span className="font-normal normal-case text-slate-400">· private to your organization</span></p>
              <div className="space-y-2" data-testid="person-notes">
                {p.notes.length === 0 && <span className="text-sm text-slate-400">No notes yet</span>}
                {p.notes.map((n) => (
                  <div key={n.id} className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    <div className="flex items-start justify-between gap-2"><p className="whitespace-pre-wrap">{n.body}</p>
                      {canEdit && <button onClick={() => rmNote(n.id)} className="shrink-0 text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}</div>
                    <p className="mt-1 text-[11px] text-slate-400">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</p>
                  </div>
                ))}
              </div>
              {canEdit && (
                <div className="mt-2 flex gap-2">
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note…" data-testid="note-input" className="flex-1" />
                  <Button size="sm" onClick={addNote} data-testid="note-add"><Plus className="h-4 w-4" /></Button>
                </div>
              )}
            </section>

            {canEdit && (
              <div className="border-t border-slate-100 pt-4">
                {p.status === "active" ? (
                  <Button variant="ghost" size="sm" onClick={deactivate} data-testid="person-archive" className="text-red-600 hover:bg-red-50">Archive this person</Button>
                ) : (
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={reactivate} data-testid="person-reactivate">Restore to active</Button>
                    <span className="text-xs text-slate-400">This person is archived.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------- People page ----------------------------- */
export default function PeoplePage({ api, active, caps }) {
  const tenantId = active?.tenant_id;
  const canView = caps.includes("people:view");
  const canCreate = caps.includes("people:create");
  const canTags = caps.includes("people:manage_tags");
  const canImport = caps.includes("import:manage");
  const [tab, setTab] = useState("directory");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [classTypes, setClassTypes] = useState([]);
  const [tags, setTags] = useState([]);
  const searchTimer = useRef(null);

  const fetchPage = useCallback(async (search, cur, append) => {
    setLoading(true);
    try {
      const params = { limit: 25 };
      if (search) params.q = search;
      if (cur) params.cursor = cur;
      const r = await api.get(`/tenants/${tenantId}/people`, { params });
      setItems((prev) => append ? [...prev, ...r.data.items] : r.data.items);
      setNextCursor(r.data.next_cursor);
      setHasMore(r.data.has_more);
    } catch (e) { if (e?.response?.status !== 403) toast.error("Could not load people"); }
    finally { setLoading(false); }
  }, [api, tenantId]);

  useEffect(() => {
    if (!tenantId || !canView) { setLoading(false); return; }
    fetchPage("", null, false);
    api.get(`/classification-types`).then((r) => setClassTypes(r.data)).catch(() => {});
    if (canTags || canView) api.get(`/tenants/${tenantId}/tags`).then((r) => setTags(r.data)).catch(() => {});
  }, [tenantId, canView, canTags, fetchPage]);

  const onSearch = (val) => {
    setQ(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setCursor(null); fetchPage(val, null, false); }, 300);
  };
  const reload = () => fetchPage(q, null, false);

  if (!canView) {
    return (
      <div className="space-y-6">
        <PageHeader title="People" subtitle={active?.tenant_name} />
        <AccessDenied message="You don't have access to your organization's People directory. Ask an administrator if you need access." />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="people-page">
      <PageHeader title="People" subtitle={active?.tenant_name}
        action={tab === "directory" && canCreate && <Button onClick={() => setShowAdd(true)} data-testid="add-person-button"><UserPlus className="mr-1.5 h-4 w-4" />Add person</Button>} />

      {canImport && (
        <div className="flex gap-1 border-b border-slate-200" data-testid="people-tabs">
          {[["directory", "Directory"], ["imports", "Imports"]].map(([k, l]) => (
            <button key={k} data-testid={`people-tab-${k}`} onClick={() => setTab(k)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{l}</button>
          ))}
        </div>
      )}

      {tab === "imports" ? (
        <ImportsPage api={api} tenantId={tenantId} caps={caps} />
      ) : (<>
      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <TextInput value={q} onChange={(e) => onSearch(e.target.value)} placeholder="Search by name, email, phone…" className="pl-9" data-testid="people-search" />
      </div>

      {loading && items.length === 0 ? <Loading label="Loading people…" /> : items.length === 0 ? (
        <EmptyState icon={Contact} title={q ? "No matching people" : "Build your people network"}
          description={q ? "Try a different search." : "Keep track of the people your organization works with — supporters, volunteers, committee members, prospects, and other contacts."}
          action={canCreate && !q && <Button onClick={() => setShowAdd(true)} data-testid="empty-add-person"><UserPlus className="mr-1.5 h-4 w-4" />Add a person</Button>}
          testid="people-empty" />
      ) : (
        <Card>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2.5 font-semibold">Name</th>
                  <th className="hidden px-4 py-2.5 font-semibold sm:table-cell">Status</th>
                  <th className="px-4 py-2.5 font-semibold text-right">&nbsp;</th>
                </tr></thead>
                <tbody data-testid="people-list">
                  {items.map((p) => (
                    <tr key={p.id} className="cursor-pointer border-b border-slate-50 last:border-0 hover:bg-slate-50" onClick={() => setSelected(p.id)} data-testid={`person-row-${p.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{p.display_name}</td>
                      <td className="hidden px-4 py-3 sm:table-cell"><Badge variant={p.status === "active" ? "implemented" : "neutral"}>{p.status}</Badge></td>
                      <td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelected(p.id); }} data-testid={`view-person-${p.id}`}>View</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="border-t border-slate-100 p-3 text-center">
                <Button variant="outline" size="sm" onClick={() => { setCursor(nextCursor); fetchPage(q, nextCursor, true); }} data-testid="people-load-more">Load more</Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}
      </>)}

      <AddPersonModal api={api} tenantId={tenantId} open={showAdd} onClose={() => setShowAdd(false)} onCreated={(id) => { reload(); setSelected(id); }} classTypes={classTypes} />
      {selected && <PersonDetail api={api} tenantId={tenantId} personId={selected} caps={caps} tags={tags} classTypes={classTypes} onClose={() => setSelected(null)} onChanged={reload} />}
    </div>
  );
}
